import { addNotification } from '@frontend/core/lib/modules/notifications'
import { useAppDispatch } from '@frontend/react'
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@frontend/ui'
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react'
import { AlertTriangle, ChevronDown, RefreshCw } from 'lucide-react'
import { ReactNode, useState } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
  fallbackTitle?: string
  showCard?: boolean
  className?: string
  useUIFallback?: boolean
}

interface ErrorDisplayProps {
  error: Error
  resetError: () => void
  fallbackTitle: string
  showCard: boolean
  className?: string
}

function ErrorDisplay({
  error,
  resetError,
  fallbackTitle,
  showCard,
  className
}: ErrorDisplayProps) {
  const [showDebug, setShowDebug] = useState(false)

  const content = (
    <div className={`space-y-4 ${className || ''}`}>
      <Alert variant='destructive'>
        <AlertTriangle className='h-4 w-4' />
        <AlertDescription>
          Something went wrong. Please try refreshing or contact support if the
          problem persists.
        </AlertDescription>
      </Alert>

      <div className='flex items-center justify-between'>
        <Button
          onClick={resetError}
          variant='outline'
          size='sm'
          className='flex items-center gap-2'
        >
          <RefreshCw className='h-4 w-4' />
          Try Again
        </Button>

        <Button
          onClick={() => setShowDebug(!showDebug)}
          variant='ghost'
          size='sm'
          className='text-muted-foreground flex items-center gap-2'
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${
              showDebug ? 'rotate-180' : ''
            }`}
          />
          Debug Info
        </Button>
      </div>

      {showDebug && (
        <div className='space-y-2'>
          <div className='text-muted-foreground text-sm font-medium'>
            Error Details:
          </div>
          <div className='bg-muted rounded-md p-3 font-mono text-sm'>
            <div className='text-destructive font-semibold'>{error.name}</div>
            <div className='mt-1'>{error.message}</div>
            {error.stack && (
              <details className='mt-2'>
                <summary className='text-muted-foreground hover:text-foreground cursor-pointer'>
                  Stack Trace
                </summary>
                <pre className='text-muted-foreground mt-2 text-xs whitespace-pre-wrap'>
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  )

  if (!showCard) {
    return content
  }

  return (
    <Card className={className}>
      <CardHeader className='pb-3'>
        <CardTitle className='text-destructive flex items-center gap-2 text-lg'>
          <AlertTriangle className='h-5 w-5' />
          {fallbackTitle}
        </CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  )
}

export const ErrorBoundary = ({
  children,
  fallbackTitle = 'Something went wrong',
  showCard = true,
  className,
  useUIFallback = false
}: ErrorBoundaryProps) => {
  const dispatch = useAppDispatch()

  const errorHandler = (
    error: unknown,
    componentStack: string,
    eventId: string
  ) => {
    console.log('ErrorBoundary error:', error)

    if (!useUIFallback) {
      dispatch(
        addNotification({
          title: `Unexpected error occurred. You will be redirected to the home page`,
          displayType: 'modal',
          message: error instanceof Error ? error.message : String(error),
          type: 'error',
          action: {
            label: 'Go to Home',
            onClick: () => {
              window.location.href = '/'
            }
          }
          // TODO: All issues are reported to Sentry automatically for now //
          // We can add a "Report Issue" action later if needed //
          // secondaryAction: {
          //   label: 'Report Issue',
          //   onClick: () => {
          //     console.log('Report issue clicked')
          //   }
          // }
        })
      )
    }
  }

  return (
    <SentryErrorBoundary
      onError={errorHandler}
      fallback={
        useUIFallback
          ? ({ error, resetError }) => (
              <ErrorDisplay
                error={error as Error}
                resetError={resetError}
                fallbackTitle={fallbackTitle}
                showCard={showCard}
                className={className}
              />
            )
          : undefined
      }
    >
      {children}
    </SentryErrorBoundary>
  )
}
